import React, { useState } from 'react'
import moment from 'moment'
import DashboardEditableRow from '@components/pages/Dashboard/DashboardEditableRow.component'
import {
  ARAgingMetricCard,
  AROver90DayCard,
  CollectionRateMetricCard,
  ProductionPerProviderCard,
} from '@components/pages/Dashboard/Finance/SectionCards'
import { SeatUtilizationCard } from '@components/pages/Dashboard/Operations/SectionCards'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { DashboardSection } from '@constants/Dashboard/dashboard.enums'
import { CALENDAR_DATE_FORMAT } from '@utils/dashboardFinance.util'
import { usePerformanceDashboard } from './usePerformanceDashboard'
import {
  ClinicalEfficiencyCard,
  CollectionCard,
  DsoCard,
  NetCollectionCard,
  ProductionByDepartmentCard,
  ProductionByProcedureCard,
  ProviderUtilizationCard,
  SchedulingPerformanceCard,
  UtilizationMetrics,
} from './SectionCards'
import '@components/pages/Dashboard/DashboardLayout.style.css'
import '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.style.css'
import '../Finance/DashboardFinance.style.css'
import '../Operations/DashboardOperations.style.css'

type DashboardPerformanceProps = {
  editMode?: boolean
  hiddenSections?: Set<string>
  range?: RangeKey
  date?: string
}

function DashboardPerformance({
  editMode = false,
  hiddenSections = new Set(),
  range = '1D',
  date = moment().format(CALENDAR_DATE_FORMAT),
}: DashboardPerformanceProps): React.ReactElement {
  const show = (section: DashboardSection): boolean =>
    !hiddenSections.has(section)
  const [providerSearch, setProviderSearch] = useState('')
  const performance = usePerformanceDashboard({
    range,
    date,
    hiddenSections,
    providerSearch,
  })

  return (
    <div className="dashboard-finance dashboard-performance">
      {show(DashboardSection.UtilizationMetrics) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row"
        >
          <ClinicalEfficiencyCard
            titleHref={performance.hrefs.clinicalEfficiency}
            metrics={performance.utilizationApi.clinicalEfficiencyMetrics}
            loading={performance.utilizationApi.clinicalEfficiencyLoading}
            hasPayload={performance.utilizationApi.clinicalEfficiencyHasPayload}
            hasData={performance.utilizationApi.clinicalEfficiencyHasData}
          />
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.SchedulingPerformance) ||
        show(DashboardSection.ProductionByProcedure)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-operations-two-cards-row dashboard-performance-row-scheduling-production"
        >
          {show(DashboardSection.SchedulingPerformance) && (
            <SchedulingPerformanceCard
              titleHref={performance.hrefs.schedulingPerformance}
              range={range}
              loading={performance.schedulingPerformanceApi.loading}
              schedulingPerformanceOverview={
                performance.schedulingPerformanceApi.data
              }
            />
          )}
          {show(DashboardSection.ProductionByProcedure) && (
            <ProductionByProcedureCard
              titleHref={performance.hrefs.productionByProcedure}
              range={range}
              prodByProcedure={performance.prodByProcedureApi}
              productionPanel={performance.productionPanel}
            />
          )}
        </DashboardEditableRow>
      )}

      {show(DashboardSection.UtilizationMetrics) && (
        <UtilizationMetrics
          editMode={editMode}
          schedulingUtilizationMetrics={
            performance.utilizationApi.schedulingUtilizationMetrics
          }
          schedulingLoading={performance.utilizationApi.schedulingLoading}
          schedulingHasPayload={performance.utilizationApi.schedulingHasPayload}
          clinicalEfficiencyMetrics={
            performance.utilizationApi.clinicalEfficiencyMetrics
          }
          clinicalEfficiencyLoading={
            performance.utilizationApi.clinicalEfficiencyLoading
          }
          clinicalEfficiencyHasPayload={
            performance.utilizationApi.clinicalEfficiencyHasPayload
          }
          diagnosedTreatmentLoading={
            performance.utilizationApi.diagnosedTreatmentLoading
          }
          diagnosedTreatmentHasPayload={
            performance.utilizationApi.diagnosedTreatmentHasPayload
          }
          caseAcceptanceLoading={
            performance.utilizationApi.caseAcceptanceLoading
          }
          caseAcceptanceHasPayload={
            performance.utilizationApi.caseAcceptanceHasPayload
          }
          utilizationRow2Metrics={
            performance.utilizationApi.utilizationRow2Metrics
          }
        />
      )}

      {(show(DashboardSection.CollectionMetric) ||
        show(DashboardSection.CollectionRate) ||
        show(DashboardSection.ArAgingMetric) ||
        show(DashboardSection.ArOver90Day)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-finance-four-metrics-row"
        >
          {show(DashboardSection.CollectionMetric) && (
            <NetCollectionCard
              titleHref={performance.hrefs.collection}
              collection={performance.collectionApi}
            />
          )}
          {show(DashboardSection.CollectionRate) && (
            <CollectionRateMetricCard
              titleHref={performance.hrefs.collection}
              collection={performance.collectionApi}
            />
          )}
          {show(DashboardSection.ArAgingMetric) && (
            <ARAgingMetricCard
              titleHref={performance.hrefs.arAging}
              arAging={performance.arAgingApi}
            />
          )}
          {show(DashboardSection.ArOver90Day) && (
            <AROver90DayCard
              titleHref={performance.hrefs.arOver90Day}
              arOver90={performance.arOver90Api}
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.SeatUtilization) ||
        show(DashboardSection.ProviderUtilization)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-operations-two-cards-row"
        >
          {show(DashboardSection.SeatUtilization) && (
            <SeatUtilizationCard
              titleHref={performance.hrefs.seatUtilization}
              metrics={performance.seatUtilizationApi.cardMetrics}
              chartData={performance.seatUtilizationApi.donutSlices}
              loading={performance.seatUtilizationApi.loading}
              hasPayload={performance.seatUtilizationApi.hasPayload}
            />
          )}
          {show(DashboardSection.ProviderUtilization) && (
            <ProviderUtilizationCard
              rows={performance.utilizationApi.providerUtilizationRows}
              loading={performance.utilizationApi.providerUtilizationLoading}
              hasPayload={
                performance.utilizationApi.providerUtilizationHasPayload
              }
            />
          )}
        </DashboardEditableRow>
      )}

      {show(DashboardSection.ProductionByDepartment) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row"
        >
          <ProductionByDepartmentCard
            titleHref={performance.hrefs.productionByDepartment}
            prodByDept={performance.prodByDeptApi}
          />
        </DashboardEditableRow>
      )}

      {show(DashboardSection.ProductionPerProvider) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row"
        >
          <ProductionPerProviderCard
            titleHref={performance.hrefs.productionPerProvider}
            data={performance.prodPerProviderApi}
            providerSearch={providerSearch}
            onProviderSearchChange={setProviderSearch}
            className="dashboard-performance-full-width"
            showFilter={false}
            searchClassName="dashboard-finance-production-provider-search"
          />
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.DsoDaysToCollect) ||
        show(DashboardSection.Collection)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-finance-row dashboard-finance-collection-row"
        >
          {show(DashboardSection.DsoDaysToCollect) && (
            <DsoCard
              titleHref={performance.hrefs.collectionRatio}
              collection={performance.collectionApi}
              dsoBarChart={performance.dsoBarChart}
            />
          )}

          {show(DashboardSection.Collection) && (
            <CollectionCard
              titleHref={performance.hrefs.collection}
              range={range}
              collection={performance.collectionApi}
            />
          )}
        </DashboardEditableRow>
      )}
    </div>
  )
}

export default DashboardPerformance
